import type { CallCost, Pricing, SellingPrice } from "@voicely/zod-schemas/costing.zod";

/**
 * The pure half of the cost pages: turning what an operator types into the
 * integers the API takes, and the API's numbers back into words.
 *
 * Here rather than in the components because the unit-test config runs in
 * `node` with no DOM, so anything inside a component body is untested by
 * construction — and a rate typed as "0.0058" becoming the wrong number of
 * micros is the one mistake on these pages that costs real money.
 */

/** IST is UTC+05:30 with no daylight saving; the calculator counts days in it. */
const IST_OFFSET_MS = 330 * 60 * 1000;

/** Today in IST, `YYYY-MM-DD`. */
export function istToday(nowMs = Date.now()): string {
	return new Date(nowMs + IST_OFFSET_MS).toISOString().slice(0, 10);
}

/** The calculator's default window: the first of this month to today, in IST. */
export function monthToDate(nowMs = Date.now()): { from: string; to: string } {
	const to = istToday(nowMs);

	return { from: `${to.slice(0, 8)}01`, to };
}

/**
 * A decimal amount in currency units → integer micros, or null when it is not
 * a number or has more precision than a micro can hold.
 *
 * Parsed from the string rather than via `parseFloat(x) * 1e6`, because
 * `0.0058 * 1e6` is `5799.999999999999` in floating point — and truncating that
 * is a rate one micro short, forever.
 */
export function toMicros(input: string): number | null {
	const value = input.trim();

	if (!/^\d+(\.\d{1,6})?$/.test(value)) return null;

	const [whole = "0", fraction = ""] = value.split(".");

	return Number(whole) * 1_000_000 + Number(fraction.padEnd(6, "0"));
}

/**
 * The selling price as typed into the page — rupees per minute and a pulse in
 * seconds — or undefined while either is missing or not a number.
 *
 * Undefined rather than an error: a half-typed price is the normal state of
 * the field, and the page should keep showing cost while it is being typed.
 */
export function parseSellingPrice(
	price: string,
	pulse: string,
	firstPulse = "",
): SellingPrice | undefined {
	const pricePerMinuteMicros = toMicros(price);
	const pulseSeconds = Number(pulse);

	if (pricePerMinuteMicros === null || !Number.isInteger(pulseSeconds)) return undefined;
	if (pulseSeconds < 1 || pulseSeconds > 60) return undefined;

	const first = firstPulse.trim() === "" ? undefined : Number(firstPulse);
	if (first !== undefined && (!Number.isInteger(first) || first < 1 || first > 300))
		return undefined;

	return { pricePerMinuteMicros, pulseSeconds, firstPulseSeconds: first };
}

/** Integer micros → the decimal string an operator would type. */
export function fromMicros(micros: number): string {
	const whole = Math.floor(micros / 1_000_000);
	const fraction = String(micros % 1_000_000)
		.padStart(6, "0")
		.replace(/0+$/, "");

	return fraction ? `${whole}.${fraction}` : String(whole);
}

const perLabel = (per: number) => (per === 1 ? "" : ` per ${new Intl.NumberFormat().format(per)}`);

/** A pricing shape in one line: what it charges, in the rate's currency. */
export function describePricing(pricing: Pricing, currency: string): string {
	switch (pricing.type) {
		case "per_unit":
			return `${fromMicros(pricing.priceMicros)} ${currency}${perLabel(pricing.per)}`;
		case "flat_per_call":
			return `${fromMicros(pricing.priceMicros)} ${currency} once per call`;
		case "pulse": {
			const first = pricing.firstPulseSeconds ?? pricing.pulseSeconds;
			const minimum = pricing.minimumSeconds ? `, min ${pricing.minimumSeconds}s` : "";

			return `${fromMicros(pricing.pricePerMinuteMicros)} ${currency}/min, ${first}/${pricing.pulseSeconds}s pulse${minimum}, per ${pricing.appliesTo}`;
		}
		case "per_event_minimum":
			return `${fromMicros(pricing.priceMicros)} ${currency}${perLabel(pricing.per)}, min ${pricing.minimumPerEvent} per event`;
		case "context_tiered":
			return pricing.bands
				.map(
					(band) =>
						`${band.upToPerRequest === null ? "above" : `≤${band.upToPerRequest}`}: ${fromMicros(band.priceMicros)}${perLabel(band.per)}`,
				)
				.join("; ")
				.concat(` ${currency} per request`);
	}
}

/**
 * Where a rate sits, as a path: `stt › livekit › deepgram › nova-3-general`.
 *
 * Null is "any" and is left off; `""` is a real value — billed directly, or the
 * lane's adapter default — and is named.
 */
export function scopeLabel(scope: {
	service: string;
	gateway: string | null;
	vendor: string | null;
	model: string | null;
}): string {
	const parts: string[] = [scope.service];

	if (scope.gateway !== null) parts.push(scope.gateway === "" ? "(direct)" : scope.gateway);
	if (scope.vendor !== null) parts.push(scope.vendor);
	if (scope.model !== null) parts.push(scope.model === "" ? "(adapter default)" : scope.model);

	return parts.join(" › ");
}

/** A comma-separated URL value → its non-empty parts. */
export function splitList(value: string): string[] {
	return value
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean);
}

function csvCell(value: string | number | null | undefined): string {
	if (value === null || value === undefined) return "";

	const text = String(value);

	return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** Micros → a plain decimal for a spreadsheet: no symbol, no grouping, and blank for unknown. */
const csvMoney = (micros: number | null | undefined) =>
	micros === null || micros === undefined ? "" : (micros / 1_000_000).toFixed(6);

export const CSV_SERVICES = ["telephony", "stt", "llm", "tts", "storage"] as const;

/**
 * The part of a call's `llm` spend that post-call summary and extraction made.
 *
 * Already inside the `llm` column, so it is a column of its own for reading
 * and must never be added to the others — a sum across every column would
 * count it twice.
 */
export const postCallMicros = (call: Pick<CallCost, "byPurpose">): number | undefined =>
	call.byPurpose.post_call;

/**
 * The per-call table as CSV.
 *
 * Amounts are plain decimals in the base currency, and an unpriced leg is an
 * empty cell rather than a zero — a spreadsheet summing the column must not be
 * told a missing number was free.
 */
export function callsToCsv(calls: CallCost[], currency: string): string {
	const header = [
		"session_id",
		"started_at",
		"workspace",
		"agent",
		"batch_id",
		"direction",
		"connected",
		"connected_seconds",
		"connected_clock",
		...CSV_SERVICES.map((service) => `${service}_${currency}`),
		// Named "of_llm" so nobody sums it with the service columns.
		`post_call_of_llm_${currency}`,
		`total_${currency}`,
		`per_connected_minute_${currency}`,
		"partial",
		`carrier_reported_${currency}`,
		`revenue_${currency}`,
	];

	const rows = calls.map((call) => [
		call.sessionId,
		call.startedAt === null ? "" : new Date(call.startedAt).toISOString(),
		call.teamName ?? call.teamId ?? "",
		call.agentName ?? call.agentId ?? "",
		call.batchId ?? "",
		call.direction,
		call.connected ? "yes" : "no",
		call.connectedSeconds,
		call.connectedSource,
		...CSV_SERVICES.map((service) => csvMoney(call.byService[service])),
		csvMoney(postCallMicros(call)),
		csvMoney(call.totalMicros),
		csvMoney(call.costPerConnectedMinuteMicros),
		call.partial ? "yes" : "no",
		csvMoney(call.carrierCostMicros),
		csvMoney(call.revenueMicros ?? undefined),
	]);

	return [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\n");
}
